/*
 * Offer a plan (owner, 2026-11-02): a text with a link where a one-time home client starts their own repeating clean at
 * their home's price. Every 2 weeks first, then monthly, then weekly. Monthly repeats on the same weekday of the month
 * ("Monthly on the 1st Monday") so routes stay steady. The link is a quote made for them, kept under Offers in My Trades.
 */
import { z } from "zod";
import { rfuMoney } from "./campaigns.js";

export const PLAN_OFFER_ORDER = ["biweekly", "monthly", "weekly"] as const;
export type PlanOfferOften = (typeof PLAN_OFFER_ORDER)[number];

const OFTEN_WORDS: Record<PlanOfferOften, string> = {
  biweekly: "every 2 weeks",
  monthly: "once a month",
  weekly: "every week",
};

/**
 * Why we are texting, the first thing the text says (owner, 2026-11-02, [[feedback-offer-text-copy]]): they sent a
 * request, they texted us, their cleaner told us, or we just cleaned for them.
 */
export const PLAN_OFFER_OPENERS = ["request", "texted", "cleaner", "after"] as const;
export type PlanOfferOpener = (typeof PLAN_OFFER_OPENERS)[number];
export const PLAN_OFFER_OPENER_WORDS: Record<PlanOfferOpener, string> = {
  request: "They sent a request",
  texted: "They texted us",
  cleaner: "Their cleaner told us",
  after: "After a clean",
};

/**
 * Words that mean they want a regular cleaning, in a request or a text ("I'd love to schedule a regular clean"). One-time
 * words like "schedule" alone are left out: they book one-time cleans too.
 */
const ASK_WORDS =
  /\b(regular(ly)?|recurring|reoccurring|re-occurring|ongoing|every (2|two|other|couple( of)?) weeks?|bi-?weekly|biweekly|twice a month|monthly|once a month|every month|each month|weekly|every week|on a schedule|maintenance (plan|clean(ing)?)|cleaning plan)\b/i;
export const asksForPlan = (text: string | null | undefined) => Boolean(text && ASK_WORDS.test(text));

export const planOfferPreview = z.object({
  /** The home the price is for ("2,200 sq ft"). */
  home: z.string(),
  /** Each schedule offered, every 2 weeks first. */
  prices: z.array(z.object({ often: z.enum(PLAN_OFFER_ORDER), label: z.string(), perVisitCents: z.number() })),
  /** The first visit at the service's own price, or null when it starts at the plan price (a clean in the last 30 days). */
  firstVisitCents: z.number().nullable(),
  /** The text as it goes out, with LINK where their link goes. */
  text: z.string(),
  /** The same text with each opener, for the office to pick (cleaner only when we know who cleaned last). */
  texts: z.array(z.object({ opener: z.enum(PLAN_OFFER_OPENERS), label: z.string(), text: z.string() })).default([]),
  /** The cleaner they had last ("Kenia"): the booking page shows that cleaner's open days. */
  cleaner: z.string().nullable().default(null),
  /** An open offer they have not booked yet: sending again sends the same link. */
  openQuoteId: z.uuid().nullable(),
  /** The opener that fits: their request or text asking, else their cleaner, else after a clean. */
  suggested: z.enum(PLAN_OFFER_OPENERS).default("after"),
});
export type PlanOfferPreview = z.infer<typeof planOfferPreview>;

export const planOfferAnswer = z.object({ quoteId: z.uuid(), text: z.string() });

/** Send the offer: the text as the office edited it, with LINK where their link goes (added at the end when left out). */
export const planOfferSend = z
  .object({
    text: z.string().trim().min(10, "Type the text.").max(640, "Make the text shorter.").optional(),
    /** The request it answers: it moves to Quote sent with this offer on it. */
    leadId: z.uuid().optional(),
  })
  .strict();
export type PlanOfferSend = z.infer<typeof planOfferSend>;
export type PlanOfferAnswer = z.infer<typeof planOfferAnswer>;

/** "We can come every 2 weeks for $198 a visit, or once a month for $231." The first two only. */
export function planPricesWords(prices: readonly { often: PlanOfferOften; perVisitCents: number }[]): string {
  const [a, b] = prices;
  if (!a) return "";
  const first = `We can come ${OFTEN_WORDS[a.often]} for ${rfuMoney(a.perVisitCents)} a visit`;
  return b ? `${first}, or ${OFTEN_WORDS[b.often]} for ${rfuMoney(b.perVisitCents)}.` : `${first}.`;
}

/** The soft last line with their link (owner, 2026-11-02): no "book now", they look when they like. */
export const pickYourDay = (link: string) => `Your prices and our open days are here if you'd like to take a look ${link}`;

/**
 * The offer text, the way the owner likes it ([[feedback-offer-text-copy]]): why we are texting, a yes or no question,
 * two prices, then the link. No brand name (they know us). A plan that does not start at the plan price says the first
 * visit too.
 */
export function planOfferText(p: {
  first: string;
  prices: readonly { often: PlanOfferOften; perVisitCents: number }[];
  firstVisitCents: number | null;
  link: string;
  opener?: PlanOfferOpener;
  cleaner?: string | null;
  /** The day we last cleaned, for After a clean ("Nov 1"). */
  cleanedOn?: string | null;
}): string {
  const why =
    p.opener === "request"
      ? "we saw your request for a regular cleaning."
      : p.opener === "texted"
        ? "you asked about a regular cleaning."
        : p.opener === "cleaner" && p.cleaner
          ? `${p.cleaner} let us know you might like a regular cleaning.`
          : p.cleanedOn
            ? `thanks for having us on ${p.cleanedOn}.`
            : "here are your prices for a regular cleaning.";
  const first = p.firstVisitCents === null ? "" : ` Your first visit is ${rfuMoney(p.firstVisitCents)}.`;
  return `Hi ${p.first}, ${why} Would you like to be on a plan? ${planPricesWords(p.prices)}${first} ${pickYourDay(p.link)}`;
}

/** The office's own words for the offer: LINK becomes their link, or the link line goes at the end. */
export function offerTextWithLink(text: string, link: string): string {
  const t = text.trim();
  return t.includes("LINK") ? t.split("LINK").join(link) : `${t} ${pickYourDay(link)}`;
}
